import { createClient } from "@connectrpc/connect";
import {
  BaseGrpcCatalogService,
  GrpcCatalogoFetchResult,
} from "../../../../base/services/BaseGrpcCatalogService";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import {
  CatalogosMonitoreoService,
  MonitoreoCultivoPlagaItem,
} from "../../../../generated/grpc/catalogos_monitoreo_pb";
import { registrarTablaPorCultivo } from "../../../monitoreo/sesion/semaforo";

/**
 * Valores de referencia **por cultivo**: la tabla B de la especificación v1.0 (3.2 B).
 *
 * Es lo que permite que limón, naranja y mandarina tengan umbrales propios. El catálogo
 * de plagas (`monitoreoPlagasEnfermedades`) tiene una sola fila "Cítricos" compartida por
 * los tres, con un solo `rangoBajo`/`rangoMedio`, así que leer de ahí daba el mismo umbral
 * para cultivos con presiones distintas.
 *
 * **Para qué la usa la web**: evaluar las sesiones que **no** traen referencias congeladas
 * —las anteriores a que el servidor empezara a guardarlas—. La especificación (5.2, última
 * fila) manda caer a los valores vigentes del cultivo en ese caso, y esta lista es "los
 * valores vigentes del cultivo". Las sesiones nuevas no la necesitan: traen su propia copia
 * congelada.
 */
export class MonitoreoCultivoPlagaGrpcCatalogService extends BaseGrpcCatalogService<MonitoreoCultivoPlagaItem> {
  protected readonly storeName = "monitoreoCultivoPlagas" as const;
  protected readonly sortFieldName = "orden";

  protected async fetchAsync(ultimaSincronizacion: bigint): Promise<GrpcCatalogoFetchResult<MonitoreoCultivoPlagaItem>> {
    const client = createClient(CatalogosMonitoreoService, grpcTransportProvider.get());

    try {
      const response = await client.listMonitoreoCultivoPlagas({ ultimaSincronizacion });
      return { items: response.items, timestampSincronizacion: response.timestampSincronizacion };
    } catch (error) {
      throw mapConnectError(error);
    }
  }
}

/** Singleton del servicio gRPC de la tabla por cultivo. */
export const monitoreoCultivoPlagaGrpcCatalogService = new MonitoreoCultivoPlagaGrpcCatalogService();

/**
 * Carga la tabla por cultivo y la deja lista para el motor de evaluación.
 *
 * Lo llaman las tres pantallas que evalúan —mapa semanal, análisis histórico e
 * informe— antes de usar el motor. Nunca falla hacia arriba: si no se pudo traer,
 * el motor sigue con el camino que ya tenía (el catálogo viejo). Dejar caer la
 * pantalla entera por un catálogo de respaldo sería peor que mostrarla con el
 * umbral compartido.
 */
export async function cargarTablaPorCultivoAsync(): Promise<void> {
  try {
    const items = await monitoreoCultivoPlagaGrpcCatalogService.getListAsync();
    registrarTablaPorCultivo(items);
  } catch (error) {
    console.warn(
      "[monitoreoCultivoPlaga] No se pudo cargar la tabla por cultivo; se evalúa con los umbrales del catálogo:",
      error,
    );
  }
}
